/** Searching change control, and attaching a record to a work item.
 *
 * Every call goes to our own API, never to the change system directly — that is what keeps
 * the API key out of the browser bundle.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface ChangeSummary {
  externalId: string
  number: string
  title: string
  status: string
  url: string
  changeType: string
  riskLevel: string
  assignee: string
  scheduledStart: string | null
}

/** Search results, plus whether change control is configured at all.
 *
 * `available: false` is a normal answer, not an error: an environment without a CAB key
 * must still show the Workbench, and the picker says so rather than looking broken.
 */
interface SearchResult {
  available: boolean
  changes: ChangeSummary[]
}

export function useChangeSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: ['change-search', query],
    enabled,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/changes/search', {
        params: { query: { q: query, limit: 15 } },
      })
      return data as unknown as SearchResult
    },
  })
}

function useInvalidateWorkItems() {
  const client = useQueryClient()
  // Work items carry their linked change, so both the board and any list of them are stale
  // the moment a link changes.
  return () => void client.invalidateQueries({ queryKey: ['work-items'] })
}

export function useLinkChange() {
  const invalidate = useInvalidateWorkItems()
  return useMutation({
    mutationFn: async ({
      itemId,
      externalId,
      rowVersion,
    }: {
      itemId: string
      externalId: string
      rowVersion: number
    }) => {
      const { data } = await api.PUT('/api/v1/work-items/{item_id}/change', {
        params: { path: { item_id: itemId } },
        body: { external_id: externalId, row_version: rowVersion } as never,
      })
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUnlinkChange() {
  const invalidate = useInvalidateWorkItems()
  return useMutation({
    mutationFn: async ({ itemId, rowVersion }: { itemId: string; rowVersion: number }) => {
      const { data } = await api.DELETE('/api/v1/work-items/{item_id}/change', {
        params: { path: { item_id: itemId }, query: { row_version: rowVersion } },
      })
      return data
    },
    onSuccess: invalidate,
  })
}

export function useRefreshChange() {
  const invalidate = useInvalidateWorkItems()
  return useMutation({
    mutationFn: async (itemId: string) => {
      const { data } = await api.POST('/api/v1/work-items/{item_id}/change/refresh', {
        params: { path: { item_id: itemId } },
      })
      return data
    },
    onSuccess: invalidate,
  })
}
